/**
 * SAM-588 — rate groups (Internacional / Dominicano / Propietario / Socio).
 *
 * Maps the form state to/from `service.pricing.rate_groups` and each booking option's
 * `prices_by_group`, the shape `functions/booking/pricing.py` reads:
 *
 *   rate_groups: [{ id, label: {en, es}, eligibility: {type: 'public'|'document'|'role'|'member', ...},
 *                   is_default, max_qty_per_booking, max_qty_per_day }]
 *   booking_options[].prices_by_group: { [groupId]: number }   // empty cell = not sold to that group
 *
 * Precedence (server): rate groups win over `prices_by_role`. An option with a non-empty
 * `prices_by_group` is priced only through groups; other options keep role/flat pricing.
 * Pure functions, no React, so the money-shaping rules are unit-testable.
 */

export type RateGroupEligibilityType = 'public' | 'document' | 'role' | 'membership' | 'member';

export interface RateGroupEligibility {
  type: RateGroupEligibilityType;
  document?: string;
  roles?: string[];
}

export interface ServiceRateGroup {
  id: string;
  label?: { en?: string; es?: string };
  eligibility: RateGroupEligibility;
  is_default?: boolean;
  max_qty_per_booking?: number;
  max_qty_per_day?: number;
}

/** Numbers stay strings so an empty field is distinguishable from a typed 0. */
export interface RateGroupForm {
  id: string;
  label_en: string;
  label_es: string;
  eligibility_type: RateGroupEligibilityType;
  /** `document` eligibility: the verified document type, e.g. "cedula". */
  document: string;
  /** `role` eligibility: comma-separated role names or user_type ids, e.g. "owner, resident, 3". */
  roles: string;
  is_default: boolean;
  max_qty_per_booking: string;
  max_qty_per_day: string;
  /** Form-only: the id still follows Label (EN). True for a new group until the id is typed by
   * hand; never set for a saved group, whose id stored bookings and cells already reference. */
  id_auto?: boolean;
}

export const ELIGIBILITY_LABELS: Record<RateGroupEligibilityType, string> = {
  public: 'Public (anyone)',
  document: 'Verified document (cédula)',
  role: 'Role (owner / resident)',
  membership: 'Property membership (approved)',
  member: 'Member',
};

export function emptyRateGroup(): RateGroupForm {
  return {
    id: '', label_en: '', label_es: '', eligibility_type: 'public',
    document: 'cedula', roles: '', is_default: false, max_qty_per_booking: '', max_qty_per_day: '',
    id_auto: true,
  };
}

export function parseRoles(raw: string): string[] {
  return raw.split(',').map((r) => r.trim()).filter(Boolean);
}

export function rateGroupsToForm(groups: ServiceRateGroup[] | undefined): RateGroupForm[] {
  return (groups || []).map((g) => {
    const type = (g.eligibility?.type || 'public') as RateGroupEligibilityType;
    return {
      id: g.id || '',
      label_en: g.label?.en || '',
      label_es: g.label?.es || '',
      eligibility_type: type,
      document: g.eligibility?.document || (type === 'document' ? 'cedula' : 'cedula'),
      roles: (g.eligibility?.roles || []).map(String).join(', '),
      is_default: Boolean(g.is_default),
      max_qty_per_booking: g.max_qty_per_booking != null ? String(g.max_qty_per_booking) : '',
      max_qty_per_day: g.max_qty_per_day != null ? String(g.max_qty_per_day) : '',
    };
  });
}

/**
 * Only groups with a non-blank, unique id are sent: the server identifies a group by id, and a
 * half-filled row is not a rule. Blank quotas are omitted (0 would mean "nothing may be booked").
 * At most one group keeps `is_default` (the first one flagged).
 */
export function rateGroupsToPayload(forms: RateGroupForm[]): ServiceRateGroup[] {
  const seen = new Set<string>();
  let defaultTaken = false;
  const out: ServiceRateGroup[] = [];
  for (const g of forms) {
    const id = g.id.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);

    const label: { en?: string; es?: string } = {};
    if (g.label_en.trim()) label.en = g.label_en.trim();
    if (g.label_es.trim()) label.es = g.label_es.trim();

    let eligibility: RateGroupEligibility = { type: g.eligibility_type };
    if (g.eligibility_type === 'document') eligibility = { type: 'document', document: g.document.trim() || 'cedula' };
    if (g.eligibility_type === 'role') eligibility = { type: 'role', roles: parseRoles(g.roles) };
    // SAM-626: any approved property membership qualifies; roles (optional) narrow which count.
    if (g.eligibility_type === 'membership') {
      const roles = parseRoles(g.roles);
      eligibility = roles.length ? { type: 'membership', roles } : { type: 'membership' };
    }

    const isDefault = g.is_default && !defaultTaken;
    if (isDefault) defaultTaken = true;

    out.push({
      id,
      ...(Object.keys(label).length ? { label } : {}),
      eligibility,
      ...(isDefault ? { is_default: true } : {}),
      ...(g.max_qty_per_booking.trim() !== '' ? { max_qty_per_booking: Number(g.max_qty_per_booking) } : {}),
      ...(g.max_qty_per_day.trim() !== '' ? { max_qty_per_day: Number(g.max_qty_per_day) } : {}),
    });
  }
  return out;
}

export function pricesByGroupToForm(cells: Record<string, number> | undefined): Record<string, string> {
  return Object.fromEntries(Object.entries(cells || {}).map(([gid, price]) => [gid, String(price)]));
}

/** Empty cells are dropped (= not sold to that group) and cells of unknown groups are pruned. */
export function pricesByGroupToPayload(
  cells: Record<string, string> | undefined,
  validGroupIds: Set<string>,
): Record<string, number> {
  const out: Record<string, number> = {};
  Object.entries(cells || {}).forEach(([gid, value]) => {
    if (!validGroupIds.has(gid)) return;
    if (value === '' || value == null) return;
    out[gid] = Number(value) || 0;
  });
  return out;
}

/** Human-readable problems to show next to the editor (not blocking: invalid rows are dropped on save). */
export function rateGroupIssues(forms: RateGroupForm[]): string[] {
  const issues: string[] = [];
  const ids = forms.map((g) => g.id.trim());
  if (ids.some((id) => !id)) issues.push('A group without an ID will not be saved.');
  const dup = ids.filter((id, i) => id && ids.indexOf(id) !== i);
  if (dup.length) issues.push(`Duplicate group ID: ${Array.from(new Set(dup)).join(', ')}.`);
  if (forms.filter((g) => g.is_default).length > 1) issues.push('Only one group can be the default.');
  forms.forEach((g) => {
    if (g.eligibility_type === 'role' && parseRoles(g.roles).length === 0) {
      issues.push(`${g.id || 'A group'}: role eligibility needs at least one role.`);
    }
  });
  return issues;
}

/** Slug for a group id derived from a label, e.g. "Dominicano" -> "dominicano". */
export function slugifyGroupId(label: string): string {
  return label.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Toggle one role (user_type id) in the comma-separated `roles` field, keeping any other value. */
export function toggleRole(raw: string, roleId: string): string {
  const roles = parseRoles(raw);
  return (roles.includes(roleId) ? roles.filter((r) => r !== roleId) : [...roles, roleId]).join(', ');
}
